<template>
    <div class="verification-form-wrapper">
        <div
            v-if="facilityLogoMediaUrl"
            class="flex justify-content-center mt-4"
        >
            <img :src="facilityLogoMediaUrl" class="max-w-full">
        </div>
        <div
            v-if="!!formTitle && currentStep !== Step.FormError"
            class="grid justify-content-center my-4"
        >
            <div :class="currentColumnClasses">
                <h2>{{ formTitle }}</h2>
            </div>
        </div>
        
        <div class="grid justify-content-center">
            <div :class="currentColumnClasses">
                <p-card 
                    v-if="currentStep === Step.PatientVerification"
                    class="form-card"
                >
                    <template #title>
                        Verify your Identity
                    </template>
                    <template #content>
                        <p>We value your privacy. Please verify your last name and date of birth to continue:</p>
                        <form-field
                            v-slot="{ onBlur }"
                            :validation-state="patientIdentityValidationState.result.value.lastName"
                            label-key="labels.portalIntake.lastName"
                            required
                        >
                            <p-input-text
                                v-model="patientIdentity.lastName"
                                maxlength="255"
                                @blur="onBlur"
                            />
                        </form-field>
                        <form-field
                            v-slot="{ onBlur }"
                            :validation-state="patientIdentityValidationState.result.value.dateOfBirth"
                            label-key="labels.portalIntake.dob"
                            required
                        >
                            <p-input-mask
                                id="patient-intake-dob"
                                v-model="dateOfBirth"
                                mask="99/99/9999"
                                placeholder="mm/dd/yyyy"
                                slot-char="mm/dd/yyyy"
                                @blur="onBlur"
                            />
                        </form-field>
                    </template>
                    <template #footer>
                        <p-button
                            type="submit" 
                            @click="submitVerification"
                        >
                            Verify
                        </p-button>
                    </template>
                </p-card>
                
                <p-card 
                    v-if="currentStep === Step.CodeVerification"
                    class="form-card"
                >
                    <template #title>
                        Enter your Confirmation Code
                    </template>
                    <template #content>
                        <p>
                            We've just sent you a six digit confirmation code. Enter it below to continue:
                        </p>

                        <form-field label-key="labels.portalIntake.confirmationCode" required>
                            <p-input-number
                                v-model="validationCode"
                                :format="false" 
                                :min="0"
                                :max="999999"
                            />
                        </form-field>

                        <small>If you don't receive the code within five minutes, refresh and try again.</small>
                    </template>
                    <template #footer>
                        <p-button
                            type="submit"
                            @click="loadForm()"
                        >
                            Confirm
                        </p-button>
                    </template>
                </p-card>
                
                <p-card v-if="currentStep === Step.ResendInviteConfirmed">
                    <template #title>
                        New Link Sent
                    </template>
                    <template #content>
                        <p>A new link has been sent. Please check your email to continue.</p>
                    </template>
                </p-card>
                
                <p-card v-if="currentStep === Step.ResendInvite" class="form-card mt-4">
                    <template #title>
                        Link Expired
                    </template>
                    <template #content>
                        <p>This link has expired. Click below to resend a new link to the email address on file:</p>
                    </template>
                    <template #footer>
                        <p-button type="submit" @click="resendInvite">
                            Resend Link
                        </p-button>
                    </template>
                </p-card>

                <p-card v-if="currentStep === Step.FormError" class="mt-4 form-card">
                    <template #title>
                        Page Not Available
                    </template>
                    <template
                        v-if="verifyType === VerificationType.Intake || verifyType === VerificationType.Invite"
                        #content
                    >
                        <p>
                            This link is no longer available. If you need assistance, contact your provider. Click below
                            to log in to the portal:
                        </p>
                    </template>
                    <template
                        v-if="verifyType === VerificationType.Intake || verifyType === VerificationType.Invite"
                        #footer
                    >
                        <p-button 
                            label="Log In"
                            @click="router.push({ path: '/login', query: { facilityType: ExternalFacilityType.NhMessaging } })"
                        />
                    </template>
                </p-card>
                
                <div v-if="currentStep === Step.FormEntry">
                    <slot />
                </div>
            </div>
        </div>
        
        <div v-if="!!facilityName && currentStep !== Step.FormError">
            <div class="flex justify-content-center mt-2">
                <div :class="currentColumnClasses" class="text-center">
                    {{ facilityName }} <span v-if="facilityPhoneNumber"> · {{ facilityPhoneNumber }}</span> <span
                        v-if="facilityAddressLine"
                    > · {{ facilityAddressLine }}</span>
                </div>
            </div>
            <div class="flex justify-content-center verification-subfooter">
                <div :class="currentColumnClasses" class="text-center">
                    Powered by <a href="https://www.nethealth.com/">Net Health</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, onMounted, ref } from "vue";
import { DateTime } from "luxon";
import axios from "axios";
import { parseDate } from "@/utils";
import {
    Alert,
    ContactAuthorizationViewModel,
    LookupTableViewModel,
    PortalFacilityDetailsViewModel, 
    PortalFacilityViewModel
} from "@/store/classes";
import { useRootStore } from "@/store";
import { VerificationFormStep as Step, VerificationType } from "@/store/enums";
import FormField from "@/components/common/FormField.vue3.vue";
import { z } from "zod";
import { RequiredDateSchema, RequiredStringSchema } from "@/components/common/schemas";
import { useFormValidation } from "@/components/common/FormValidation";
import { useRouter } from 'vue-router';
import { ExternalFacilityType } from "@/store/enums";

const props = defineProps<{
    contactKey: string;
    verifyType: VerificationType;
    formTitle: string;
    columnClasses?: string;
}>();

const emit = defineEmits<{
    (e: 'load-form', val: { 
        validationCode?: string,
        patientIdentity?: ContactAuthorizationViewModel,
        currentStep: Ref<Step>,
        facilityDetails: PortalFacilityDetailsViewModel 
    }): void;
}>();

const router = useRouter();
const rootStore = useRootStore();

const currentColumnClasses = computed(() => {
    return (currentStep.value === Step.FormEntry && !!props.columnClasses)
        ? props.columnClasses
        : 'lg:col-6 xl:col-5'
})

const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const initializePortal = (contactKey: string, facility: PortalFacilityViewModel) => rootStore.dispatch('initializePortal', { contactKey, facility });

const currentStep = ref<Step>(Step.FormLoading);
const facilityDetails = ref<PortalFacilityDetailsViewModel>({
    lookupTables: [] as LookupTableViewModel[]
} as PortalFacilityDetailsViewModel);

const validationCode = ref<number | undefined>(undefined);
const patientIdentity = ref({
    lastName: '',
    dateOfBirth: ''
});
const patientIdentitySchema = z.object({
    lastName: RequiredStringSchema,
    dateOfBirth: RequiredDateSchema,
});
const patientIdentityValidationState = useFormValidation<z.infer<typeof patientIdentitySchema>>(
    patientIdentitySchema, 
    patientIdentity);

const facilityName = computed(() => 
    facilityDetails.value.facilityLocation?.facilityName || facilityDetails.value.facility?.facilityName);

const facilityPhoneNumber = computed(() =>
    facilityDetails.value.facilityLocation?.phoneNumber || facilityDetails.value.facility?.phoneNumber);

const facilityAddressLine = computed(() =>
    facilityDetails.value.facilityLocation?.addressLine || facilityDetails.value.facility?.addressLine);

const facilityLogoMediaUrl = computed(() =>
    facilityDetails.value.facility?.logoGuid 
        ? process.env.VUE_APP_PORTAL_BASE_URL + '/media/' + encodeURIComponent(facilityDetails.value.facility.logoGuid) 
        : ''
);

const formTitle = computed<string>(() => {
    if (!facilityName.value) 
        return ''; // default to blank until we have a facility
    const suffix = facilityDetails.value?.facility ? ' for ' + facilityName.value : '';
    return props.formTitle + suffix;
})

const dateOfBirth = computed({
    get: () => {
        const dateOfBirth = patientIdentity.value.dateOfBirth || '';
        const date = DateTime.fromISO(dateOfBirth);
        return date.isValid ? date.toFormat('MM/dd/yyyy') : dateOfBirth;
    },
    set: (value) => {
        const parsed = parseDate(value);
        patientIdentity.value.dateOfBirth = parsed.isValid ? parsed.toISODate() : value || '';
    }
});

onMounted(async () => {
    document.title = formTitle.value;
    await showLoader('contact-verification');
    try {
        if (props.verifyType === VerificationType.FacilityVisitIntake) {
            currentStep.value = Step.FacilityCodeVerification;
            return;
        }
        const response = await axios.get<PortalFacilityDetailsViewModel>(
            `/portal/verification/${props.verifyType}/${encodeURIComponent(props.contactKey)}/facility`);
        if (response.status === 204) {
            currentStep.value = Step.ResendInvite;
        } else if (response.status !== 200) {
            throw 'Invalid invite';
        } else {
            currentStep.value = Step.PatientVerification;
            facilityDetails.value = response.data;
            await initializePortal(props.contactKey, response.data.facility);
            document.title = formTitle.value;
        }
    } catch (e) {
        currentStep.value = Step.FormError;
    } finally {
        await hideLoader('contact-verification');
    }
});

async function submitVerification(): Promise<void> {
    if (!patientIdentityValidationState.validate()) return;
    try {
        await showLoader('contact-verification');
        const response = await axios.post(
            `/portal/verification/${props.verifyType}/${encodeURIComponent(props.contactKey)}/authorize`, patientIdentity.value);
        if (response.status === 204) {
            currentStep.value = Step.ResendInvite;
        } else if (response.status !== 200) {
            throw 'Invalid invite';
        } else {
            if (props.verifyType === VerificationType.Portal || props.verifyType === VerificationType.Survey) {
                // skip confirmation code
                await loadForm(); 
            } else {
                currentStep.value = Step.CodeVerification;
            }
        }
    } catch (e) {
        await showAlert({ text: 'Unable to verify identity, please try again', type: 'error' });
    } finally {
        await hideLoader('contact-verification');
    }
}

async function loadForm(): Promise<void> {
    emit('load-form', {
        currentStep,
        validationCode: validationCode.value?.toString(), 
        patientIdentity: patientIdentity.value,
        facilityDetails: facilityDetails.value 
    });
}

async function resendInvite(): Promise<void> {
    await showLoader('contact-verification');
    try {
        const baseURL = props.verifyType === VerificationType.Intake ? '/portal/intake/' : '/auth/invite/';
        await axios.post(`${baseURL}${props.contactKey}/resend`);
    } catch (e) {
        await showAlert({ text: 'Unable to resend link, please contact the system administrator', type: 'error' });
        return;
    } finally {
        await hideLoader('contact-verification');
    }
    currentStep.value = Step.ResendInviteConfirmed;
}
</script>

<style lang="scss" scoped>
:deep(.verification-subfooter) {
    color: #999;
}

:deep(.verification-form-wrapper .grid > *) {
    max-width: 1000px;
}
</style>
